<script setup lang="ts">
import { Section, Button } from "@/components/Ui";
import { onDashboard } from "@/helpers/events";
import {
  BlockTransparentModel
} from "@/components/Blocks";
import { getPrivateQueryParam } from "~/helpers/private";

import { onUnmounted, onMounted } from "vue";
import { isMobile } from "@/helpers/breakpoints";
import PrivateCardBins from "~/components/Sections/PrivateCardBins.vue";
import Icon from "@/components/Ui/Icon/Icon.vue";

const { $gsap: gsap, $ScrollTrigger: ScrollTrigger } = useNuxtApp();
const { t } = useI18n();
const ctx = ref<any>(null);
const animation = () =>
  gsap.context(() => {
    // Title
    gsap.set("#private-best-terms .title", {
      y: "-55%",
      zIndex: -1,
      filter: "blur(8px)"
    });
    gsap.to("#private-best-terms .title", {
      y: 0,
      duration: 5,
      zIndex: 1,
      opacity: 1,
      filter: "blur(0px)",
      ease: "sine.out",
      scrollTrigger: {
        trigger: "#app",
        // pin: true,
        scrub: 0.1,
        start: "300px top",
        end: "700px top"
        // markers: true
      }
    });
    gsap.to("#private-best-terms .title", {
      duration: 5,
      filter: "blur(0px)",
      ease: "sine.out",
      scrollTrigger: {
        trigger: "#app",
        // pin: true,
        scrub: 0.1,
        start: "300px top",
        end: "500px top"
        // markers: true
      }
    });
    gsap.set("#private-best-terms .title", {
      y: "0",
      zIndex: -1,
      filter: "blur(0px)"
    });
    gsap.to("#private-best-terms .title", {
      y: "240%",
      // duration: 2.5,
      zIndex: -1,
      filter: "blur(20px)",
      scrollTrigger: {
        trigger: "#private-best-terms",
        // pin: true,
        scrub: 0.1,
        start: "top 100px"
        // markers: true
      }
    });

    // Desktop - 1,2 block, mobile - 1 block
    if (!isMobile.value) {
      const firstBlocks = [
        "#private-best-terms .second",
        "#private-best-terms .third"
      ];
      gsap.set(firstBlocks, { y: 500, opacity: 1 });
      firstBlocks.forEach((block: any, i: number) => {
        gsap.to(block, {
          y: 0,
          duration: 3,
          scrollTrigger: {
            trigger: block,
            // pin: true,
            // markers: true,
            scrub: 0.1,
            start: i === 0 ? "-100% bottom" : "-30% bottom",
            end: i === 0 ? "-100px bottom" : "30% bottom",
            id: String(i)
          }
        });
      });
    }

    if (!isMobile.value) {
      const firstBlocks = [
        "#private-best-terms .four",
        "#private-best-terms .five"
      ];
      gsap.set(firstBlocks, { y: 500, opacity: 1 });
      firstBlocks.forEach((block: any, i: number) => {
        gsap.to(block, {
          y: 0,
          duration: 3,
          scrollTrigger: {
            trigger: block,
            // pin: true,
            // markers: true,
            scrub: 0.1,
            start: i === 0 ? "-100% bottom" : "-30% bottom",
            end: i === 0 ? "-100px bottom" : "30% bottom",
            id: String(i)
          }
        });
      });
    }

    // Desktop - null block, mobile - 2 block
    const toUpBlocks = isMobile.value
      ? [
        "#private-best-terms .first",
        "#private-best-terms .second",
        "#private-best-terms .third",
        "#private-best-terms .four",
        "#private-best-terms .five"
      ]
      : ["#private-best-terms .first"];
    gsap.set(toUpBlocks, { autoAlpha: 0, y: 50 });
    toUpBlocks.forEach((block: any, i: number) => {
      gsap.to(block, {
        autoAlpha: 1,
        duration: 3,
        y: 0,
        scrollTrigger: {
          trigger: block,
          end: "200px bottom",
          // once: true,
          scrub: 0.1
          // markers: true,
        }
      });
    });
  });
onMounted(() => {
  setTimeout(() => {
    ctx.value = gsap.context(() => animation());
  }, 500);
});
onUnmounted(() => {
  ctx.value.revert();
});
</script>
<template>
  <Section
    id="private-best-terms"
    :title-class="`${$style[`root-title`]} ${
      $style[`root-title-${$i18n.locale}`]
    } title`"
    :class="$style.root"
  >
    <template #title>
      {{ t("section.privateBestTerms") }}
    </template>
    <div :class="`${$style[`root-wrapper`]}`">
      <BlockTransparentModel class="first" />

      <!--
      <BlockFreeCardsV4 class="second" />
      <BlockOneDollarV2 class="third" />
      -->

      <PrivateCardBins class="second">

        <div class="flex flex-none">
          <Icon name="private-facebook-logo" style="width: 80%;" />
        </div>
        <div class="flex flex-none mt-5" :class="$style['private-cards-bins-count']">
          20 Bins
        </div>
      </PrivateCardBins>

      <PrivateCardBins class="third">
        <div class="flex flex-none">
          <Icon name="private-google-logo" style="width: 80%;display:block;" />
        </div>
        <div class="flex flex-none mt-5" :class="$style['private-cards-bins-count']">
          10 Bins
        </div>
      </PrivateCardBins>

      <PrivateCardBins class="four">
        <div class="flex flex-none mb-2">
          <Icon name="private-tiktok-logo" style="width: 80%;display:block;" />
        </div>
        <div class="flex flex-none mt-5" :class="$style['private-cards-bins-count']">
          10 Bins
        </div>
      </PrivateCardBins>
      <PrivateCardBins class="five">
        <div class="flex flex-none mb-2">
          <span
            :class="[$style['private-cards-bins-title'], $style[`private-cards-bins-title-${$i18n.locale}`]]">{{ $t("block.privateBestTerms.advertisingCard") }}</span>
        </div>
        <div class="flex flex-none mt-5" :class="$style['private-cards-bins-count']">
          5 Bins
        </div>
      </PrivateCardBins>

    </div>
    <Button
      :title="$t('btn.joinToPstPriivate')"
      color="orange"
      :class="$style[`root-button`]"
      @click="
        onDashboard(
          'landing_privat_button-2',
          `/register?${getPrivateQueryParam()}`,
          {
            // 'cta type': 'button',
            // 'cta location': 'Hero:PST Private',
            // 'cta text': 'Join to PST Private',
          },
          {},
          true
        )
      "
    />
  </Section>
</template>

<style lang="scss" module>
.private-cards-bins-title {
  font-family: ALS Granate VF;
  font-size: 62px;
  font-style: normal;
  font-weight: 700;
  line-height: 100%; /* 62px */
  background: linear-gradient(278deg, rgba(0, 0, 0, 0.60) -10.94%, rgba(255, 255, 255, 0.65) 48.99%, rgba(255, 255, 255, 0.28) 105.3%);
  background-clip: text;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;


  &-bg,
  &-th,
  &-hi,
  &-ua,
  &-zh,
  &-bg,
  &-ja,
  &-th,
  &-hi,
  &-ar,
  &-bn,
  &-ko,
  &-vi,
  &-sw {
    @apply text-[48px] xxs:text-[58px] 414:text-[62px];
  }
  &-sw {
    @apply pb-2 translate-y-2;
  }
  &-hi,
  &-bn {
    @apply leading-[1.3];
  }
  &-pl,
  &-be,
  &-ru {
    @apply text-[44px] xxs:text-[54px] 414:text-[62px] md:text-[56px] lg:text-[62px];
  }
  &-es,
  &-en,
&-id,
&-da,
&-et,
&-hu,
&-lt,
&-nb,
&-ro,
&-sl,
&-cs,
&-el,
&-fi,
&-lv,
&-nl,
&-sk,
&-sv,
&-he,
  &-fr,
  &-pt {
    @apply text-[40px] xxs:text-[50px] 414:text-[60px] md:text-[54px] lg:text-[62px];
  }

  &-it {
    @apply text-[38px] xxs:text-[46px] 414:text-[54px] md:text-[50px] lg:text-[62px];;
  }

  &-tr {
    @apply text-[52px] xxs:text-[62px] md:text-[62px];
  }

  &-de {
    @apply text-[46px] xxs:text-[52px] 414:text-[62px] md:text-[54px] lg:text-[62px];
  }

  &-zh,
  &-ja {
    @apply leading-[1.1];
  }
}

.private-cards-bins-count {
  display: inline-flex;
  padding: 20px;
  justify-content: center;
  align-items: center;
  gap: 10px;

  height: 74px;
  width: 190px;

  background: url("@/assets/svg/icon/private-bins-border.svg");
  background-size: cover;
  padding: 3px;

  color: #FFF;
  font-family: ALS Granate VF;
  font-size: 44px;
  font-style: normal;
  font-weight: 400;
  line-height: 44px; /* 100% */
}

.root {
  @apply w-full h-auto;

  &-wrapper {
    @apply w-full grid grid-cols-2 gap-[10px];
    & > div {
      @apply col-span-2 md:col-span-1;
    }

    & > div:first-child {
      @apply md:col-span-2;
    }
  }

  &-title {
    @apply px-6 xxs:px-8 font-light text-left leading-[113%] w-full text-white opacity-0 rtl:text-right;
    &-en,
&-id,
&-da,
&-et,
&-hu,
&-lt,
&-nb,
&-ro,
&-sl,
&-cs,
&-el,
&-fi,
&-lv,
&-nl,
&-sk,
&-sv,
&-he,
    &-bg,
    &-tr,
    &-zh,
    &-th,
    &-pl,
    &-hi,
    &-ar,
    &-bn,
    &-ko,
    &-sw{
      @apply text-[34px] xxs:text-[40px] xs:text-[44px] md:text-[72px] lg:text-[100px];
    }

    &-ua {
      @apply text-[44px] xxs:text-[54px] xs:text-[60px] md:text-[72px] lg:text-[100px];
    }

    &-pt {
      @apply text-[26px] xxs:text-[30px] xs:text-[36px] md:text-[56px] lg:text-[76px] xl:text-[80px];
    }
    &-vi,
    &-it,
    &-fr,
    &-es {
      @apply text-[26px] xxs:text-[30px] xs:text-[36px] md:text-[56px] lg:text-[76px] xl:text-[86px];
    }

    &-de {
      @apply text-[28px] xxs:text-[34px] xs:text-[40px] md:text-[62px] lg:text-[86px];
    }
    &-be,
    &-ru {
      @apply text-[28px] xxs:text-[34px] xs:text-[40px] md:text-[82px] lg:text-[100px];
    }

    &-ja {
      @apply text-[34px] xxs:text-[40px] xs:text-[44px] md:text-[72px] lg:text-[100px];
    }
  }

  &-button {
    @apply w-full max-w-[440px] mx-auto;
  }
}
</style>
